import { ATHENS_GRAY } from "@/controls/shared/constants/colors";
import {
	ErrorModal,
	HelmetDexteel,
	dxtToLocalServerTime,
	getMomentTz,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Grid2 as Grid, Typography } from "@mui/material";
import { GridApi } from "ag-grid-community";
import { get } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import {
	LazyBackDropLoading,
	LazyLoading,
} from "../../../controls/LazyLoading";
import { DynamicGenericReportFilter } from "../models/GenericReportFilter";
import { GenericReportHeader } from "../models/GenericReportHeader";
import { GenericReportFilterDataSource } from "../models/GenericReportMetadataFilter";
import {
	getGenericReportByCode,
	getGenericReportData,
} from "../repositories/genericReport";
import { useGenericReportContext } from "./GenericReportContext";
import { ReportFilterPanel } from "./components/ReportFilterPanel";
import { ReportTable } from "./components/ReportTable";

const moment = getMomentTz();

// functions
const getTimeOfDay = (date: Date, init: boolean): Date => {
	const newDate = moment(date).startOf("day");
	if (!init) {
		newDate.endOf("day");
	}
	return newDate.toDate();
};
export const GenericReportPage = () => {
	const { state, actions } = useGenericReportContext();
	const {
		state: { settings },
	} = useUTLSettingsContext();
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const gridRef = useRef<GridApi>(null);
	const [columnHeaders, setColumnHeaders] = useState<
		GenericReportHeader[] | null
	>(null);

	const isSlagReport = state.reportCode === "mes-lab-slag-samples";
	const isLastEAFSamples = state.reportCode === "mes-lab-last-eaf-samples";

	const NUMBER_OF_HEATS = get(settings, "LAST_EAF_SAMPLES_NUMBER_OF_HEATS", 20);

	const [rows, setRows] = useState<any[]>([]);
	const [firstLoad, setFirstLoad] = useState(false);
	const [error, setError] = useState<string>("");
	const [reportFilters, setReportFilters] =
		useState<DynamicGenericReportFilter>({
			startDate: moment.utc().subtract(1, "days").toDate(),
			endDate: moment.utc().toDate(),
			showStartDate: false,
			showEndDate: false,
			IncludeExternal: false,
			onLoad: false,
		});
	const updateFilters = (newValue: DynamicGenericReportFilter) => {
		setReportFilters(newValue);
	};
	//Url parameters
	const setUrlParameters = () => {
		const paths = location.pathname.split("/");
		let newUrl =
			(paths[0] !== undefined && paths[0] !== "/" ? `${paths[0]}` : ``) +
			(paths[1] !== undefined && paths[1] !== "/" ? `/${paths[1]}` : ``);

		newUrl += `/${state.reportCode}`;
		if (reportFilters.showStartDate)
			newUrl += `/${dxtToLocalServerTime(reportFilters.startDate, "yyyyMMdd")}`;
		if (reportFilters.showEndDate)
			newUrl += `/${dxtToLocalServerTime(reportFilters.endDate, "yyyyMMdd")}`;
		Object.keys(reportFilters).forEach((key) => {
			if (
				key !== "startDate" &&
				key !== "endDate" &&
				key !== "showStartDate" &&
				key !== "showEndDate" &&
				key !== "IncludeExternal" &&
				key !== "onLoad"
			)
				newUrl += `/${
					reportFilters[key] === "" ||
					reportFilters[key] === null ||
					reportFilters[key] === undefined
						? "0"
						: reportFilters[key]
				}`;
		});
		window.history.replaceState(null, "", newUrl);
	};
	//Refresh data
	const refreshData = async () => {
		setUrlParameters();
		await getDataFromAPI();
	};
	//API call
	const getReportInfoFromAPI = async () => {
		actions.setIsLoadingPage(true);
		const resp = await getGenericReportByCode(
			state.reportCode === null ? "" : state.reportCode,
		);
		if (
			resp.ok &&
			resp.data.tables.length > 0 &&
			resp.data.tables[0].rows.length > 0
		) {
			const row = get(resp, "data.tables[0].rows[0]", {});
			actions.setReportInfo(row);

			if (resp.ok && resp.data.tables.length > 1) {
				const headers = get(resp, "data.tables[1].rows", []);
				actions.setReportMetadataFilters(headers);
			} else {
				actions.setReportMetadataFilters([]);
			}
			const filterSources: GenericReportFilterDataSource[][] = [];
			for (let i = 2; i < resp.data.tables.length; i++) {
				filterSources.push(resp.data.tables[i].rows);
			}
			actions.setFilterSources(filterSources);
		} else {
			console.log("something happened 1");
		}
		actions.setIsLoadingPage(false);
	};

	const processRowsForExcel = (rawRows: any[]) => {
		if (!isSlagReport) {
			return rawRows;
		}

		return rawRows.map((row) => {
			const newRow = { ...row };

			Object.keys(newRow).forEach((key) => {
				if (
					newRow[key] &&
					typeof newRow[key] === "string" &&
					newRow[key].includes("%%%")
				) {
					newRow[key] = newRow[key].split("%%%")[0];
				}
			});

			return newRow;
		});
	};

	const getDataFromAPI = async () => {
		actions.setIsLoadingGrid(true);
		if (isLastEAFSamples) reportFilters.NumberOfHeats = NUMBER_OF_HEATS;

		const resp = await getGenericReportData(
			{
				...reportFilters,
				startDate: getTimeOfDay(reportFilters.startDate, true),
				endDate: getTimeOfDay(reportFilters.endDate, false),
			},
			state.reportInfo?.DataSource ?? "",
		);
		if (resp.ok && resp.data.tables.length > 0) {
			const rows = get(resp, "data.tables[0].rows", []);
			const lowerCaseRows = rows.map((row: any) => {
				return Object.keys(row).reduce((acc: any, key) => {
					acc[key.toLowerCase()] = row[key];
					return acc;
				}, {});
			});
			setRows(lowerCaseRows);
		}
		if (resp.ok && resp.data.tables.length > 1) {
			const headers = get(resp, "data.tables[1].rows", []);
			const lowerCaseHeaders = headers.map((header: GenericReportHeader) => {
				return {
					...header,
					ColumnData: header.ColumnData.toLowerCase(),
				};
			});
			setColumnHeaders(lowerCaseHeaders);
		} else {
			setColumnHeaders([]);
		}
		if (!resp.ok) {
			setError(resp.message);
		}
		actions.setIsLoadingGrid(false);
	};
	// useEffects
	useEffect(() => {
		// Load report info
		(async () => {
			if (state.reportCode !== "" && state.reportCode !== null)
				await getReportInfoFromAPI();
		})();
	}, [state.reportCode]);
	useEffect(() => {
		// Load default filters
		const tempFilters: DynamicGenericReportFilter = {
			...reportFilters,
			showStartDate: state.reportInfo?.HasStartDate ?? false,
			showEndDate: state.reportInfo?.HasEndDate ?? false,
			onLoad: false,
		};
		setReportFilters(tempFilters);
	}, [state.reportInfo]);
	useEffect(() => {
		// Load other filters if the report has them
		if (state.reportMetadataFilters === null) return;
		const tempFilters: DynamicGenericReportFilter = {
			...reportFilters,
			onLoad: true,
		};
		state.reportMetadataFilters?.forEach((item) => {
			if (item.Parameter === "IncludeExternal") {
				tempFilters[item.Parameter] = true;
			} else {
				tempFilters[item.Parameter] = "";
			}
		});
		setReportFilters(tempFilters);
	}, [state.reportMetadataFilters]);
	useEffect(() => {
		// call refreshData for first time and load from url parameters
		if (reportFilters.onLoad === true) {
			const tempFilters: DynamicGenericReportFilter = {
				...reportFilters,
				onLoad: false,
			};
			// Load from url parameters
			let indexParam = 1; //Starts with 1, 0 for reportCode
			Object.keys(tempFilters).forEach((key) => {
				if (
					key === "showStartDate" ||
					key === "showEndDate" ||
					key === "IncludeExternal" ||
					key === "onLoad" ||
					key === "refreshData"
				) {
				} else if (
					key === "startDate" &&
					tempFilters.showStartDate &&
					state.reportParameters[indexParam] !== undefined
				) {
					const momentDate = moment(
						state.reportParameters[indexParam],
						"YYYYMMDD",
					);
					if (momentDate.isValid()) {
						tempFilters[key] = momentDate.toDate();
					}
					indexParam++;
				} else if (
					key === "endDate" &&
					tempFilters.showEndDate &&
					state.reportParameters[indexParam] !== undefined
				) {
					const momentDate = moment(
						state.reportParameters[indexParam],
						"YYYYMMDD",
					);
					if (momentDate.isValid()) {
						tempFilters[key] = momentDate.toDate();
					}
					indexParam++;
				} else if (state.reportParameters[indexParam] !== undefined) {
					tempFilters[key] = state.reportParameters[indexParam];
					indexParam++;
				} else if (key === "IncludeExternal") {
					tempFilters[key] = false;
					indexParam++;
				} else if (key !== "startDate" && key !== "endDate") {
					tempFilters[key] = "0";
					indexParam++;
				}
			});
			setReportFilters(tempFilters);
			setFirstLoad(true);
		}
	}, [reportFilters]);
	useEffect(() => {
		(async () => {
			if (firstLoad && gridRef.current) {
				actions.setIsLoadingBackground(true);
				gridRef.current.showLoadingOverlay();
				await refreshData();
				actions.setIsLoadingBackground(false);
				gridRef.current.hideOverlay();
			}
		})();
	}, [reportFilters, firstLoad, gridApi]);
	if (!state.isLoadingPage) {
		return (
			<>
				<HelmetDexteel title={state.reportInfo?.Title || "LAB Report"} />
				<LazyBackDropLoading open={state.isLoadingBackground} />
				<Grid
					container
					justifyContent="center"
					alignItems="center"
					style={{ backgroundColor: ATHENS_GRAY }}
				>
					<Grid size={{ xs: 12 }} style={{ margin: 0, padding: "20px" }}>
						<Grid size={{ xs: 12 }}>
							<Grid
								container
								direction="row"
								justifyContent="space-between"
								alignItems="center"
							>
								<Typography variant="h5" style={{ fontWeight: 600 }}>
									{state.reportInfo?.Title}
								</Typography>
							</Grid>
						</Grid>
						<ReportFilterPanel
							metadataFilters={state.reportMetadataFilters ?? []}
							filters={reportFilters}
							filterSources={state.filterSources ?? []}
							updateFilters={updateFilters}
							refreshData={refreshData}
							onExcelExport={() => {
								if (gridRef.current) {
									const processedRows = processRowsForExcel(rows);
									gridRef.current.setGridOption("rowData", processedRows);
									gridRef.current.exportDataAsExcel({
										fileName: state.reportInfo?.Title,
										sheetName: state.reportInfo?.Title,
									});
									gridRef.current.setGridOption("rowData", rows);
								}
							}}
						/>
						<ReportTable
							headers={columnHeaders ?? []}
							rows={rows}
							refreshData={refreshData}
							gridRef={gridRef}
							loading={state.isLoadingGrid}
						/>
					</Grid>
				</Grid>
				<ErrorModal error={error} onHide={() => setError("")} />
			</>
		);
	}
	return <LazyLoading />;
};
